import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Text, Card, Button, ActivityIndicator, DataTable, Searchbar, Menu } from 'react-native-paper';
import PeriodoChip from '../../components/PeriodoChip';
import { Ionicons } from '@expo/vector-icons';
import { formatCurrency } from '../../utils/formatters';
import api from '../../services/api';
import { exportService } from '../../services/exportService';

const PERIODOS = [
  { id: 'hoy', label: 'Hoy' }, { id: 'esta_semana', label: 'Esta Semana' },
  { id: 'este_mes', label: 'Este Mes' }, { id: 'ultimo_trimestre', label: 'Último Trimestre' },
  { id: 'este_ano', label: 'Este Año' },
];

const ReporteGananciaVendedorScreen = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuVisible, setMenuVisible] = useState(false);
  const [periodoActivo, setPeriodoActivo] = useState('este_mes');
  const [sortField, setSortField] = useState('ganancia_bruta');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => { cargarReporte(); }, []);
  useEffect(() => { filtrarDatos(); }, [searchQuery, data]);

  const cargarReporte = async (periodo = periodoActivo) => {
    try {
      setLoading(true);
      const response = await api.get(`/reportes/ganancia-por-vendedor?periodo=${periodo}`);
      setData(response);
      setFilteredData(response.vendedores || []);
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudo cargar el reporte');
    } finally { setLoading(false); }
  };

  const cambiarPeriodo = (p) => { setPeriodoActivo(p); cargarReporte(p); };

  const filtrarDatos = () => {
    if (!data?.vendedores) { setFilteredData([]); return; }
    if (!searchQuery.trim()) { setFilteredData(data.vendedores); return; }
    const q = searchQuery.toLowerCase();
    setFilteredData(data.vendedores.filter(v =>
      (v.vendedor_nombre || '').toLowerCase().includes(q) ||
      (v.email || '').toLowerCase().includes(q)
    ));
  };

  const ordenarDatos = (field) => {
    const dir = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field); setSortDirection(dir);
    const sorted = [...filteredData].sort((a, b) => {
      const aV = parseFloat(a[field]) || 0;
      const bV = parseFloat(b[field]) || 0;
      return dir === 'asc' ? aV - bV : bV - aV;
    });
    setFilteredData(sorted);
  };

  const exportarDatos = async (formato) => {
    setMenuVisible(false);
    if (!filteredData.length) return Alert.alert('Error', 'No hay datos');
    const headers = ['Vendedor', 'Email', 'Ventas', 'Ingresos', 'Costos', 'Ganancia', 'Margen %', 'Clientes', 'Productos'];
    const exportData = filteredData.map(v => ({
      'Vendedor': v.vendedor_nombre, 'Email': v.email, 'Ventas': v.numero_ventas,
      'Ingresos': parseFloat(v.total_ingresos || 0).toFixed(2),
      'Costos': parseFloat(v.total_costos || 0).toFixed(2),
      'Ganancia': parseFloat(v.ganancia_bruta || 0).toFixed(2),
      'Margen %': `${v.margen_porcentaje}%`,
      'Clientes': v.clientes_atendidos, 'Productos': v.productos_vendidos,
    }));
    await exportService.exportData(formato, exportData, 'ganancia_por_vendedor', headers, 'Reporte de Ganancia por Vendedor');
  };

  const totales = data?.totales || {};

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Ganancia por Vendedor</Text>
          <Text style={styles.subtitle}>Rentabilidad real de cada vendedor</Text>
        </View>
        <Menu visible={menuVisible} onDismiss={() => setMenuVisible(false)}
          anchor={<Button icon="download" mode="outlined" onPress={() => setMenuVisible(true)} disabled={loading}>Exportar</Button>}>
          <Menu.Item title="Excel" onPress={() => exportarDatos('excel')} />
          <Menu.Item title="PDF" onPress={() => exportarDatos('pdf')} />
          <Menu.Item title="CSV" onPress={() => exportarDatos('csv')} />
        </Menu>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtrosContainer} contentContainerStyle={styles.filtrosContent}>
        <View style={styles.filtros}>
          {PERIODOS.map(p => (
            <PeriodoChip
              key={p.id}
              active={periodoActivo === p.id}
              onPress={() => !loading && cambiarPeriodo(p.id)}
            >
              {p.label}
            </PeriodoChip>
          ))}
        </View>
      </ScrollView>

      {loading ? (
        <View style={styles.loader}>
          <ActivityIndicator size="large" color="#0066cc" />
          <Text style={{ marginTop: 10, color: '#666' }}>Generando reporte...</Text>
        </View>
      ) : (
      <ScrollView>
        {/* KPIs */}
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.cardTitle}>Resumen General</Text>
            <View style={styles.kpiGrid}>
              <View style={[styles.kpiItem, { borderLeftColor: '#007bff' }]}>
                <Text style={[styles.kpiValue, { color: '#007bff' }]}>{formatCurrency(totales.total_ingresos)}</Text>
                <Text style={styles.kpiLabel}>Ingresos Totales</Text>
              </View>
              <View style={[styles.kpiItem, { borderLeftColor: '#dc3545' }]}>
                <Text style={[styles.kpiValue, { color: '#dc3545' }]}>{formatCurrency(totales.total_costos)}</Text>
                <Text style={styles.kpiLabel}>Costos Totales</Text>
              </View>
              <View style={[styles.kpiItem, { borderLeftColor: '#28a745' }]}>
                <Text style={[styles.kpiValue, { color: '#28a745' }]}>{formatCurrency(totales.ganancia_total)}</Text>
                <Text style={styles.kpiLabel}>Ganancia Total</Text>
              </View>
              <View style={[styles.kpiItem, { borderLeftColor: '#ffc107' }]}>
                <Text style={[styles.kpiValue, { color: parseFloat(totales.margen_general) >= 20 ? '#28a745' : '#ffc107' }]}>
                  {totales.margen_general}%
                </Text>
                <Text style={styles.kpiLabel}>Margen General</Text>
              </View>
            </View>
          </Card.Content>
        </Card>

        <Searchbar placeholder="Buscar vendedor..." onChangeText={setSearchQuery} value={searchQuery} style={styles.searchbar} />

        {/* Tarjetas por vendedor */}
        {filteredData.map((v, idx) => {
          const margen = parseFloat(v.margen_porcentaje) || 0;
          const margenColor = margen >= 20 ? '#28a745' : margen >= 10 ? '#ffc107' : '#dc3545';

          return (
            <Card key={idx} style={[styles.vendedorCard, { borderLeftColor: margenColor, borderLeftWidth: 4 }]}>
              <Card.Content>
                <View style={styles.vendedorHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.vendedorNombre}>{v.vendedor_nombre}</Text>
                    <Text style={styles.vendedorEmail}>{v.email}</Text>
                  </View>
                  <View style={[styles.margenBadge, { backgroundColor: margenColor + '20' }]}>
                    <Text style={[styles.margenText, { color: margenColor }]}>{v.margen_porcentaje}%</Text>
                  </View>
                </View>

                <View style={styles.vendedorMetrics}>
                  <View style={styles.metricItem}>
                    <Text style={styles.metricLabel}>Ingresos</Text>
                    <Text style={[styles.metricValue, { color: '#007bff' }]}>{formatCurrency(v.total_ingresos)}</Text>
                  </View>
                  <View style={styles.metricItem}>
                    <Text style={styles.metricLabel}>Costos</Text>
                    <Text style={[styles.metricValue, { color: '#dc3545' }]}>{formatCurrency(v.total_costos)}</Text>
                  </View>
                  <View style={styles.metricItem}>
                    <Text style={styles.metricLabel}>Ganancia</Text>
                    <Text style={[styles.metricValue, { color: '#28a745' }]}>{formatCurrency(v.ganancia_bruta)}</Text>
                  </View>
                </View>

                <View style={styles.vendedorFooter}>
                  <Text style={styles.footerText}>{v.numero_ventas} ventas</Text>
                  <Text style={styles.footerText}>{v.clientes_atendidos} clientes</Text>
                  <Text style={styles.footerText}>{v.productos_vendidos} productos</Text>
                  <Text style={styles.footerText}>{v.unidades_vendidas} uds.</Text>
                </View>
              </Card.Content>
            </Card>
          );
        })}

        {filteredData.length === 0 && (
          <View style={styles.empty}><Ionicons name="trending-up-outline" size={50} color="#ccc" /><Text style={{ color: '#999', marginTop: 10 }}>Sin datos</Text></View>
        )}
        <View style={{ height: 40 }} />
      </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', padding: 16, backgroundColor: '#fff', elevation: 2 },
  title: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  subtitle: { fontSize: 14, color: '#666', marginTop: 2 },
  filtrosContainer: { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee', height: 56, flexGrow: 0, flexShrink: 0 },
  filtrosContent: { alignItems: 'center', paddingVertical: 10 },
  filtros: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16 },
  chip: { marginRight: 8, backgroundColor: '#f0f0f0' },
  chipActivo: { backgroundColor: '#4CAF50' },
  chipTexto: { color: '#333' },
  chipTextoActivo: { color: '#fff', fontWeight: '600' },
  card: { margin: 16, marginBottom: 8, elevation: 2 },
  cardTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 12 },
  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  kpiItem: { flex: 1, minWidth: '45%', backgroundColor: '#f9f9f9', padding: 12, borderRadius: 6, borderLeftWidth: 3 },
  kpiValue: { fontSize: 16, fontWeight: '700' },
  kpiLabel: { fontSize: 10, color: '#888', marginTop: 2 },
  searchbar: { margin: 16, marginBottom: 8 },
  vendedorCard: { marginHorizontal: 16, marginBottom: 10, elevation: 2, borderRadius: 8 },
  vendedorHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  vendedorNombre: { fontSize: 16, fontWeight: '700', color: '#333' },
  vendedorEmail: { fontSize: 12, color: '#888' },
  margenBadge: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16 },
  margenText: { fontSize: 16, fontWeight: '700' },
  vendedorMetrics: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  metricItem: {},
  metricLabel: { fontSize: 10, color: '#888', textTransform: 'uppercase' },
  metricValue: { fontSize: 14, fontWeight: '700' },
  vendedorFooter: { flexDirection: 'row', justifyContent: 'space-between', paddingTop: 8, borderTopWidth: 1, borderTopColor: '#f0f0f0' },
  footerText: { fontSize: 11, color: '#888' },
  empty: { alignItems: 'center', padding: 40 },
});

export default ReporteGananciaVendedorScreen;
